import _ from 'lodash'
import {
  ScopeDefinition,
  ScopeDefinitionsMap,
  RoleScopeDefinition,
  RoleScopeDefinitionsMap
} from '../types/scope.types'

/**
 * Scope definitions for the Backoffice UI
 * Each scope grants specific permissions to edit organization data
 * Note: Read-only access is the default - no explicit scope needed
 */
/** Sentinel value used by ScopeFilter to filter for users with no scopes at all */
export const NO_SCOPES_FILTER = '__no_scopes__'

export const SCOPE_DEFINITIONS: ScopeDefinitionsMap = {
  admin: {
    name: 'admin',
    label: 'Administrator',
    description: 'Full administrative access to all backoffice features',
    location: 'All Pages'
  },
  edit_org_ff: {
    name: 'edit_org_ff',
    label: 'Edit Feature Flags',
    // Turning a flag on or off for an org moved to the Google group roles — this
    // scope only covers the flag's own metadata now.
    description: 'Edit flag descriptions and backoffice visibility',
    location: 'Organization Details → Feature Flags, Feature Flags page'
  },
  edit_org_settings: {
    name: 'edit_org_settings',
    label: 'Edit Settings',
    description: 'Modify organization settings and configurations',
    location: 'Organization Details → Settings'
  },
  edit_pdt_config: {
    name: 'edit_pdt_config',
    label: 'Edit Projects',
    description: 'Modify project configurations and mappings',
    location: 'Projects Page / Organization Details → PDT Projects'
  }
}

/**
 * Role scopes, mirrored onto the account by a daily sync of the four backoffice
 * Google Workspace groups. They share the `backoffice_scopes` array with the
 * capability scopes above but are managed by IT, so the backoffice shows them
 * read-only.
 *
 * Deliberately a separate map: every capability list in the app (the edit-scopes
 * checkboxes, the permissions tables) is built from SCOPE_DEFINITIONS, and none
 * of them should grow a role row.
 *
 * `description` restates FEATURE_FLAG_ENABLEMENT_ROLES in prose — keep the two in
 * step when the enablement matrix changes.
 */
export const ROLE_SCOPE_DEFINITIONS: RoleScopeDefinitionsMap = {
  bo_engineering: {
    name: 'bo_engineering',
    label: 'Engineering',
    description:
      'Change WIP, Closed Beta, and Open Beta flags; promote early lifecycle flags.',
    group: 'bo_engineering@linearb.io'
  },
  bo_product: {
    name: 'bo_product',
    label: 'Product',
    description:
      'Change Closed Beta and Open Beta flags; promote Closed Beta to Open Beta.',
    group: 'bo_product@linearb.io'
  },
  bo_gtm: {
    name: 'bo_gtm',
    label: 'GTM',
    description: 'Change Open Beta flags for customer rollouts.',
    group: 'bo_gtm@linearb.io'
  },
  bo_techgtm: {
    name: 'bo_techgtm',
    label: 'Tech GTM',
    description: 'Change Open Beta flags for technical rollouts.',
    group: 'bo_techgtm@linearb.io'
  }
}

/**
 * Get all scope definitions as an array
 * @returns Array of scope definitions
 */
export const getAllScopeDefinitions = (): ScopeDefinition[] => {
  return Object.values(SCOPE_DEFINITIONS)
}

/**
 * Get scope definition by scope name
 * @param scopeName - The scope name (e.g., 'edit_org_ff')
 * @returns Scope definition or null if not found
 */
export const getScopeDefinition = (scopeName: string): ScopeDefinition | null =>
  getAllScopeDefinitions().find(scope => scope.name === scopeName) ?? null

/**
 * Get all role definitions as an array
 * @returns Array of role definitions
 */
export const getAllRoleScopeDefinitions = (): RoleScopeDefinition[] => {
  return Object.values(ROLE_SCOPE_DEFINITIONS)
}

/**
 * Get role definition by scope name
 * @param scopeName - The scope name (e.g., 'bo_engineering')
 * @returns Role definition or null if the name is not a role
 */
export const getRoleScopeDefinition = (
  scopeName: string
): RoleScopeDefinition | null =>
  getAllRoleScopeDefinitions().find(role => role.name === scopeName) ?? null

/**
 * Whether a scope name is a Google group–managed role rather than a capability
 */
const isRoleScope = (scopeName: string): boolean =>
  getRoleScopeDefinition(scopeName) !== null

/**
 * Split a stored `backoffice_scopes` list into the capabilities the backoffice
 * can edit and the roles it can only display.
 */
export const splitScopes = (
  scopes: string[]
): { capabilities: string[]; roles: string[] } => {
  const [roles, capabilities] = _.partition(scopes, isRoleScope)
  return { roles, capabilities }
}

/**
 * Parse a backoffice_scopes value, which arrives as a JSON string from the JWT
 * and as an array from the API.
 */
export const parseScopes = (raw: string | string[] | undefined): string[] => {
  try {
    if (!raw) {
      return []
    }
    const parsed = Array.isArray(raw) ? raw : JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}
